import type { TestSuite } from '@k-ucap/shared';
import type { TestSuiteRepository } from './RepositoryInterfaces.js';

export class InMemoryTestSuiteRepository implements TestSuiteRepository {
  private suites: Map<string, TestSuite> = new Map();

  async findAll(): Promise<TestSuite[]> {
    return Array.from(this.suites.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  async findById(id: string): Promise<TestSuite | undefined> {
    return this.suites.get(id);
  }

  async findByProjectId(projectId: string): Promise<TestSuite[]> {
    return Array.from(this.suites.values())
      .filter((s) => s.projectId === projectId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  async save(suite: TestSuite): Promise<TestSuite> {
    this.suites.set(suite.id, suite);
    return suite;
  }

  async delete(id: string): Promise<void> {
    this.suites.delete(id);
  }

  async duplicate(id: string, newName?: string): Promise<TestSuite | undefined> {
    const original = this.suites.get(id);
    if (!original) return undefined;
    const duplicated: TestSuite = {
      ...original,
      id: `suite-${Date.now()}`,
      name: newName || `${original.name} (Copy)`,
      version: this.bumpVersion(original.version),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.suites.set(duplicated.id, duplicated);
    return duplicated;
  }

  private bumpVersion(version: string): string {
    const parts = version.split('.');
    const last = parts.pop() ?? '0';
    const next = Number.isNaN(Number(last)) ? 1 : Number(last) + 1;
    return [...parts, String(next)].join('.');
  }

  seed(suites: TestSuite[]): void {
    suites.forEach((s) => this.suites.set(s.id, s));
  }
}

export const createInMemoryTestSuiteRepository = (): InMemoryTestSuiteRepository => new InMemoryTestSuiteRepository();
